import { forwardRef, useId, useState } from 'react';
import { ChevronDown, ChevronUp, Mail } from 'lucide-react';
import { Badge, Button } from '@/components/ui';
import { LoadingSpinner } from '@/components/states';
import {
  EmailSourcePanel,
  getAttachments,
} from '@/components/domain/shared/EmailSourcePanel';
import { SmartSummaryBlock } from '@/components/domain/shared/SmartSummaryBlock';
import { formatReceivedTimestamp } from '@/lib/date';
import { REVIEW_COPY } from '@/pages/review/reviewCopy';
import { NO_SUBJECT, senderNameOf } from '@/pages/review/reviewListModel';
import type { Email } from '@/types';

export interface ReviewSourcesProps {
  /** Newest first. */
  emails: readonly Email[];
  summary: string | null;
  /** The email open in place (AC14). */
  openEmailId: string | null;
  onToggleEmail(emailId: string): void;
  loading?: boolean;
  error?: boolean;
  onRetry?(): void;
  /** How many emails the item has, known before the read (default 1). */
  expectedCount?: number;
}

/**
 * The details sheet's last section (YAR-551 AC13–AC17, Figma `.Sources`):
 * `Sources · [n] email(s)`, the Smart Summary, then one numbered card per
 * email, newest first. Tapping a subject opens the email in place
 * (production's View Email Content); the open email is the parent's state
 * so the sheet can open the newest one when it lands at Sources (AC15).
 *
 * The forwarded ref is the section, the sheet's scroll target.
 */
export const ReviewSources = forwardRef<HTMLElement, ReviewSourcesProps>(
  function ReviewSources(
    {
      emails,
      summary,
      openEmailId,
      onToggleEmail,
      loading = false,
      error = false,
      onRetry,
      expectedCount = 1,
    },
    ref,
  ) {
    // R7: the header folds the section; open by default.
    const [open, setOpen] = useState(true);
    const baseId = useId();
    const bodyId = `${baseId}-body`;
    const several = emails.length > 1;

    let body = null;
    if (loading) {
      body = (
        <div
          data-testid="review-sources-loading"
          className="flex justify-center py-4"
        >
          <LoadingSpinner />
        </div>
      );
    } else if (error) {
      // Compact, section-sized: the page-level ErrorState talks about
      // "items" and pads for a whole screen.
      body = (
        <div
          role="alert"
          className="flex flex-col items-start gap-2 rounded-xl border border-border bg-surface p-4"
        >
          <p className="m-0 text-sm text-muted-foreground">
            {REVIEW_COPY.sourcesError(expectedCount)}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRetry?.()}
          >
            {REVIEW_COPY.sourcesRetry}
          </Button>
        </div>
      );
    } else if (open) {
      body = (
        <div
          id={bodyId}
          className="flex flex-col gap-3"
        >
          {summary && <SmartSummaryBlock summary={summary} />}
          {emails.map((email, index) => {
            const isOpen = openEmailId === email.id;
            const panelId = `${baseId}-email-${index}`;
            const sender = senderNameOf(email);
            const received = formatReceivedTimestamp(email.created_at);
            return (
              <div
                key={email.id}
                data-testid="review-source-email"
                className="rounded-xl border border-border bg-surface p-4 flex flex-col gap-2"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={[
                      'size-6 rounded-full text-xs font-semibold flex items-center justify-center',
                      index === 0 && several
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border text-foreground',
                    ].join(' ')}
                  >
                    {index + 1}
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {REVIEW_COPY.sourceEmail}
                  </span>
                  {index === 0 && several && (
                    <Badge
                      variant="success"
                      size="xs"
                    >
                      {REVIEW_COPY.sourceLatest}
                    </Badge>
                  )}
                </div>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => onToggleEmail(email.id)}
                  className="flex items-start gap-2 w-full bg-transparent border-none p-0 cursor-pointer text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <span className="text-base font-medium text-text-heading underline underline-offset-2 text-left break-words">
                      {email.subject?.trim() || NO_SUBJECT}
                    </span>
                    <span className="text-xs text-text-caption">
                      {sender ? `${sender} · ${received}` : received}
                    </span>
                  </span>
                  {isOpen ? (
                    <ChevronUp
                      size={20}
                      className="text-icon-default flex-shrink-0"
                    />
                  ) : (
                    <ChevronDown
                      size={20}
                      className="text-icon-default flex-shrink-0"
                    />
                  )}
                </button>
                {isOpen && (
                  <div id={panelId}>
                    <EmailSourcePanel
                      email={email}
                      attachments={getAttachments(email)}
                      hideSubject
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <section
        ref={ref}
        data-testid="review-sources"
        className="flex flex-col gap-3"
      >
        <h3 className="m-0">
          {loading || error ? (
            // No count yet (never "0 emails"), but the heading stays so
            // landing at Sources (AC15) shows where you are.
            <span className="flex items-center gap-2">
              <Mail
                size={20}
                className="text-foreground flex-shrink-0"
              />
              <span className="text-base font-semibold text-text-heading">
                {REVIEW_COPY.sourcesTitle}
              </span>
            </span>
          ) : (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={() => setOpen((o) => !o)}
              className="flex items-center gap-2 w-full bg-transparent border-none p-0 cursor-pointer text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <Mail
                size={20}
                className="text-foreground flex-shrink-0"
              />
              <span className="flex-1 min-w-0 text-base font-semibold text-text-heading">
                {REVIEW_COPY.sourcesHeading(emails.length)}
              </span>
              {open ? (
                <ChevronUp
                  size={20}
                  className="text-icon-default flex-shrink-0"
                />
              ) : (
                <ChevronDown
                  size={20}
                  className="text-icon-default flex-shrink-0"
                />
              )}
            </button>
          )}
        </h3>
        {body}
      </section>
    );
  },
);
